import React, { useMemo } from 'react';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { RebateTierProximity, ThresholdType } from '@erp/shared-types';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { InfoIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useDivisions from '@/api/organization/useDivisions';

interface TierProximityFiltersProps {
  data: RebateTierProximity[];
  filters: {
    supplierFilter: string[];
    tagFilter: string[];
    thresholdTypeFilter: string[];
    retroactiveFilter: boolean[];
    includeMissingBaseline: boolean;
    sortBy: 'closest' | 'highestDelta' | 'progress' | 'bestValue' | 'bestRatio';
    divisionFilter: string[];
    visibleColumns: string[];
  };
  onFiltersChange: (filters: {
    supplierFilter: string[];
    tagFilter: string[];
    thresholdTypeFilter: string[];
    retroactiveFilter: boolean[];
    includeMissingBaseline: boolean;
    sortBy: 'closest' | 'highestDelta' | 'progress' | 'bestValue' | 'bestRatio';
    divisionFilter: string[];
    visibleColumns: string[];
  }) => void;
}

export function TierProximityFilters({
  data,
  filters,
  onFiltersChange,
}: TierProximityFiltersProps) {
  // Fetch divisions data
  const { data: divisionsData } = useDivisions(true);

  // Get unique suppliers with their tags
  const supplierOptions = useMemo(() => {
    const supplierMap = new Map<
      string,
      { id: string; name: string; tags: Array<{ id: string; name: string }> }
    >();
    for (const item of data) {
      if (!supplierMap.has(item.supplierId)) {
        supplierMap.set(item.supplierId, {
          id: item.supplierId,
          name: item.supplierName,
          tags: item.supplierTags || [],
        });
      }
    }
    return Array.from(supplierMap.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((supplier) => ({
        id: supplier.id,
        label: supplier.name,
        value: supplier.id,
      }));
  }, [data]);

  const selectedSuppliers = useMemo(() => {
    return supplierOptions.filter((opt) =>
      filters.supplierFilter.includes(opt.id),
    );
  }, [supplierOptions, filters.supplierFilter]);

  // Get unique tags from all suppliers
  const tagOptions = useMemo(() => {
    const tagMap = new Map<string, { id: string; name: string }>();
    for (const item of data) {
      if (item.supplierTags) {
        for (const tag of item.supplierTags) {
          if (!tagMap.has(tag.id)) {
            tagMap.set(tag.id, tag);
          }
        }
      }
    }
    return Array.from(tagMap.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((tag) => ({
        id: tag.id,
        label: tag.name,
        value: tag.id,
      }));
  }, [data]);

  const selectedTags = useMemo(() => {
    return tagOptions.filter((opt) => filters.tagFilter.includes(opt.id));
  }, [tagOptions, filters.tagFilter]);

  // Threshold type options
  const thresholdTypeOptions = useMemo(
    () => [
      {
        id: ThresholdType.GROWTH,
        label: 'Growth',
        value: ThresholdType.GROWTH,
      },
      {
        id: ThresholdType.VOLUME,
        label: 'Volume',
        value: ThresholdType.VOLUME,
      },
    ],
    [],
  );

  const selectedThresholdTypes = useMemo(() => {
    return thresholdTypeOptions.filter((opt) =>
      filters.thresholdTypeFilter.includes(opt.value),
    );
  }, [thresholdTypeOptions, filters.thresholdTypeFilter]);

  // Retroactive options
  const retroactiveOptions = useMemo(
    () => [
      { id: 'true', label: 'Retroactive', value: true },
      { id: 'false', label: 'Non-Retroactive', value: false },
    ],
    [],
  );

  const selectedRetroactive = useMemo(() => {
    return retroactiveOptions.filter((opt) =>
      filters.retroactiveFilter.includes(opt.value),
    );
  }, [retroactiveOptions, filters.retroactiveFilter]);

  // Division options with grouping by tags
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  const selectedDivisions = useMemo(() => {
    return divisionOptions.filter((opt) =>
      filters.divisionFilter.includes(opt.id),
    );
  }, [divisionOptions, filters.divisionFilter]);

  const missingBaselineTooltipText =
    "Show or hide growth-based rules that don't have a growth baseline set. These rules cannot calculate tier proximity until a baseline is configured";

  // Column visibility options
  const columnOptions = useMemo(
    () => [
      { id: 'productGroups', label: 'Product Groups', value: 'productGroups' },
      { id: 'currentTier', label: 'Current Tier', value: 'currentTier' },
      { id: 'nextTier', label: 'Next Tier', value: 'nextTier' },
      {
        id: 'currentVolume',
        label: 'Current Volume',
        value: 'currentVolume',
      },
      {
        id: 'nextThreshold',
        label: 'Next Threshold',
        value: 'nextThreshold',
      },
      {
        id: 'volumeNeeded',
        label: 'Volume Needed',
        value: 'volumeNeeded',
      },
      {
        id: 'currentRebate',
        label: 'Current Rebate',
        value: 'currentRebate',
      },
      {
        id: 'nextTierRebate',
        label: 'Next Tier Rebate',
        value: 'nextTierRebate',
      },
      {
        id: 'rebateDelta',
        label: 'Rebate Delta',
        value: 'rebateDelta',
      },
      {
        id: 'netBenefit',
        label: 'Net Benefit',
        value: 'netBenefit',
      },
      {
        id: 'efficiencyRatio',
        label: 'Efficiency Ratio',
        value: 'efficiencyRatio',
      },
      { id: 'progress', label: 'Progress', value: 'progress' },
    ],
    [],
  );

  const selectedColumns = useMemo(() => {
    return columnOptions.filter((opt) =>
      filters.visibleColumns.includes(opt.value),
    );
  }, [columnOptions, filters.visibleColumns]);

  return (
    <TooltipProvider>
      <div className="flex flex-wrap items-start gap-6 p-4 bg-muted/50 rounded-lg border">
        <div className="flex flex-col gap-2 min-w-[300px]">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-medium">Suppliers</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Filter by specific suppliers to focus on their rebate tier
                  progress
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <MultiSelect
            options={supplierOptions}
            value={selectedSuppliers}
            onChange={(selected) => {
              onFiltersChange({
                ...filters,
                supplierFilter: selected.map((opt) => opt.id),
              });
            }}
            placeholder="All suppliers"
            searchPlaceholder="Search suppliers..."
            fullWidth
            maxChips={3}
            hideChipsWhenAllSelected={true}
            hideChips={true}
          />
        </div>
        {tagOptions.length > 0 && (
          <div className="flex flex-col gap-2 min-w-[300px]">
            <div className="flex items-center gap-1.5">
              <Label className="text-sm font-medium">Tags</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Filter by supplier tags to group suppliers by category or
                    attribute
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <MultiSelect
              options={tagOptions}
              value={selectedTags}
              onChange={(selected) => {
                onFiltersChange({
                  ...filters,
                  tagFilter: selected.map((opt) => opt.id),
                });
              }}
              placeholder="All tags"
              searchPlaceholder="Search tags..."
              fullWidth
              maxChips={3}
              hideChipsWhenAllSelected={true}
              hideChips={true}
            />
          </div>
        )}
        <div className="flex flex-col gap-2 min-w-[200px]">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-medium">Divisions</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Filter by divisions to focus on specific business units or
                  regions. When none are selected, all divisions are shown
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <MultiSelect
            options={divisionOptions}
            value={selectedDivisions}
            onChange={(selected) => {
              onFiltersChange({
                ...filters,
                divisionFilter: selected.map((opt) => opt.id),
              });
            }}
            placeholder="All divisions"
            searchPlaceholder="Search divisions..."
            fullWidth
            maxChips={3}
            hideChipsWhenAllSelected={true}
            hideChips={true}
          />
        </div>
        <div className="flex flex-col gap-2 min-w-[200px]">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-medium">Threshold Type</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Growth: Tier thresholds based on year-over-year growth.
                  Volume: Tier thresholds based on dollar value or flat volume
                  rebates
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <MultiSelect
            options={thresholdTypeOptions}
            value={selectedThresholdTypes}
            onChange={(selected) => {
              onFiltersChange({
                ...filters,
                thresholdTypeFilter: selected.map((opt) => opt.value),
              });
            }}
            placeholder="Threshold type"
            searchPlaceholder="Search types..."
            fullWidth
            maxChips={2}
            hideChipsWhenAllSelected={true}
            hideChips={true}
          />
        </div>
        <div className="flex flex-col gap-2 min-w-[200px]">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-medium">Retroactive</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Retroactive rules apply the tier percentage to all volume from
                  dollar one. Non-retroactive rules apply tiered rates to volume
                  brackets
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <MultiSelect
            options={retroactiveOptions}
            value={selectedRetroactive}
            onChange={(selected) => {
              onFiltersChange({
                ...filters,
                retroactiveFilter: selected.map((opt) => opt.value),
              });
            }}
            placeholder="Retroactive"
            searchPlaceholder="Search..."
            fullWidth
            maxChips={2}
            hideChipsWhenAllSelected={true}
            hideChips={true}
          />
        </div>
        <div className="flex flex-col gap-2 min-w-[250px] items-center">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-medium">Show Missing Baseline</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{missingBaselineTooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Switch
            id="includeMissingBaseline"
            checked={filters.includeMissingBaseline}
            onCheckedChange={(checked) => {
              onFiltersChange({
                ...filters,
                includeMissingBaseline: checked === true,
              });
            }}
          />
        </div>
        <div className="flex flex-col gap-2 min-w-[200px]">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-medium">Sort By</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Closest: Sort by smallest volume needed to reach next tier.
                  Highest Delta: Sort by largest potential rebate increase.
                  Progress: Sort by highest percentage complete toward next
                  tier. Best Net Benefit: Sort by highest rebate delta minus
                  volume needed. Best Efficiency Ratio: Sort by highest rebate
                  delta per dollar of volume needed
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Select
            value={filters.sortBy}
            onValueChange={(
              value:
                | 'closest'
                | 'highestDelta'
                | 'progress'
                | 'bestValue'
                | 'bestRatio',
            ) => {
              onFiltersChange({
                ...filters,
                sortBy: value,
              });
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bestValue">Best Net Benefit</SelectItem>
              <SelectItem value="bestRatio">Best Efficiency Ratio</SelectItem>
              <SelectItem value="closest">Closest Volume Needed</SelectItem>
              <SelectItem value="highestDelta">Highest Rebate Delta</SelectItem>
              <SelectItem value="progress">Progress Percentage</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2 min-w-[250px]">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-medium">Visible Columns</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Select which columns to display in the table. Rule column is
                  always visible.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <MultiSelect
            options={columnOptions}
            value={selectedColumns}
            onChange={(selected) => {
              onFiltersChange({
                ...filters,
                visibleColumns: selected.map((opt) => opt.value),
              });
            }}
            placeholder="All columns"
            searchPlaceholder="Search columns..."
            fullWidth
            maxChips={3}
            hideChipsWhenAllSelected={true}
            hideChips={true}
          />
        </div>
      </div>
    </TooltipProvider>
  );
}
